// src/components/orders/AddPaymentModal.tsx
import React, { useMemo, useState } from "react";
import Card from "../ui/Card";
import Field from "../ui/Field";
import Input from "../ui/Input";
import Textarea from "../ui/Textarea";
import Button from "../ui/Button";
import { OrderItem } from "../../services/types/orders.types";

interface AddPaymentModalProps {
    isOpen: boolean;
    order: OrderItem;
    onClose: () => void;
    onSubmit: (amount: number, comment: string) => Promise<void> | void;
}

const AddPaymentModal: React.FC<AddPaymentModalProps> = ({
                                                             isOpen,
                                                             order,
                                                             onClose,
                                                             onSubmit,
                                                         }) => {
    const [amountValue, setAmountValue] = useState("");
    const [comment, setComment] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const remainingAmount = useMemo(() => {
        return (
            Number(order.total_price || 0) - Number(order.paid_amount || 0)
        );
    }, [order.total_price, order.paid_amount]);

    const handleFillRemaining = () => {
        if (remainingAmount > 0) {
            setAmountValue(remainingAmount.toFixed(2));
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);

        const amount = Number(amountValue.replace(",", "."));
        if (!amount || amount <= 0) {
            setError("Введите сумму оплаты больше нуля");
            return;
        }

        try {
            setIsSaving(true);
            await onSubmit(amount, comment.trim());
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "Не удалось внести оплату",
            );
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-backdrop">
            <div className="modal-dialog">
                <Card className="notopacity">
                    <div className="editor-head">
                        <div>
                            <h3 className="card-title">Внести оплату</h3>
                            <p className="card-text">
                                Заказ {order.order_number}. Остаток к оплате:{" "}
                                {remainingAmount.toFixed(2)} €.
                            </p>
                        </div>
                    </div>

                    <form className="editor-form" onSubmit={handleSubmit}>
                        {error && <div className="form-global-error">{error}</div>}

                        <Field label="Сумма оплаты" htmlFor="payment-amount">
                            <div className="inline-actions">
                                <Input
                                    id="payment-amount"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={amountValue}
                                    onChange={(e) => setAmountValue(e.target.value)}
                                    placeholder={remainingAmount.toFixed(2)}
                                />
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={handleFillRemaining}
                                >
                                    Вся сумма
                                </Button>
                            </div>
                        </Field>

                        <Field label="Комментарий" htmlFor="payment-comment">
                            <Textarea
                                id="payment-comment"
                                rows={3}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Например: оплата по счёту"
                            />
                        </Field>

                        <div className="editor-actions">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={onClose}
                                disabled={isSaving}
                            >
                                Отмена
                            </Button>
                            <Button type="submit" variant="primary" disabled={isSaving}>
                                {isSaving ? "Сохраняем..." : "Внести оплату"}
                            </Button>
                        </div>
                    </form>
                </Card>
            </div>
        </div>
    );
};

export default AddPaymentModal;
